// src/pages/PrivacyPage.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="pp-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* HERO */}
      <section className="pp-hero">
        <button className="pp-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="pp-badge">Legal</div>
        <h1>Privacy <span className="grad">Policy</span></h1>
        <p className="pp-updated">Last updated: February 2026</p>
      </section>

      {/* CONTENT */}
      <article className="pp-article">
        <section className="pp-section">
          <h2>1. Introduction</h2>
          <p>
            JepTags ("we," "our," or "us") is committed to protecting the privacy of our users.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information
            when you use our product authentication platform, website, mobile applications, and
            related services (collectively, the "Services").
          </p>
        </section>

        <section className="pp-section">
          <h2>2. Information We Collect</h2>
          <p>We collect information in the following ways:</p>

          <div className="pp-subcard">
            <h3>Account Information</h3>
            <p>
              When you register for a business account, we collect your name, email address,
              company name, phone number, and business details.
            </p>
          </div>

          <div className="pp-subcard">
            <h3>Scan Data</h3>
            <p>
              When products are scanned, we collect scan location (GPS coordinates), device information,
              timestamp, and tag identification data to verify authenticity and provide analytics.
            </p>
          </div>

          <div className="pp-subcard">
            <h3>Usage Data</h3>
            <p>
              We automatically collect information about how you interact with our Services,
              including IP address, browser type, pages visited, and feature usage.
            </p>
          </div>
        </section>

        <section className="pp-section">
          <h2>3. How We Use Your Information</h2>
          <p>
            We use collected information to provide and maintain our product authentication services,
            verify product authenticity, generate analytics and reports for business clients, improve
            our platform and user experience, communicate with you about your account and services,
            comply with legal obligations, and detect and prevent fraud or unauthorized access.
          </p>
        </section>

        <section className="pp-section">
          <h2>4. Data Sharing</h2>
          <p>
            We do not sell your personal data to third parties. We may share information with service
            providers who assist in operating our platform, business clients (aggregated scan analytics
            only), law enforcement when required by law, and in connection with a merger, acquisition,
            or sale of assets with appropriate notice.
          </p>
        </section>

        <section className="pp-section">
          <h2>5. Data Security</h2>
          <p>
            We implement industry-standard security measures including end-to-end encryption, secure
            data storage with SOC 2 certified infrastructure, regular security audits and penetration
            testing, and strict access controls and authentication protocols. While we strive to protect
            your information, no method of transmission over the internet is 100% secure.
          </p>
        </section>

        <section className="pp-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide
            services. Scan data is retained for the duration of the business client's subscription
            plus 12 months. You may request deletion of your data at any time by contacting us.
          </p>
        </section>

        <section className="pp-section">
          <h2>7. Your Rights</h2>
          <p>
            Depending on your jurisdiction, you may have the right to access, correct, or delete your
            personal data, object to or restrict processing, data portability, and withdraw consent.
            To exercise these rights, contact us at{" "}
            <a href="mailto:privacy@jeptags.com">privacy@jeptags.com</a>.
          </p>
        </section>

        <section className="pp-section">
          <h2>8. International Transfers</h2>
          <p>
            JepTags operates globally with offices in the USA and Vietnam. Your data may be transferred
            to and processed in countries outside your own. We ensure appropriate safeguards are in
            place for such transfers in compliance with GDPR and other applicable data protection laws.
          </p>
        </section>

        <section className="pp-section">
          <h2>9. Contact Us</h2>
          <p>For questions about this Privacy Policy, contact us at:</p>
          <div className="pp-contact-card">
            <div className="pp-contact-name">JepTags, Inc.</div>
            <div>2635 Cunningham Ave, California, CA 95148</div>
            <div>Email: <a href="mailto:privacy@jeptags.com">privacy@jeptags.com</a></div>
            <div>Phone: <a href="tel:+18447374799">+1-844-737-4799</a></div>
          </div>
        </section>

        {/* Related links */}
        <div className="pp-related">
          <Link to="/terms" className="pp-related-link">
            Terms of Service
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </article>
    </div>
  );
}

const STYLES = `
.pp-root *,.pp-root *::before,.pp-root *::after{margin:0;padding:0;box-sizing:border-box}
.pp-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;min-height:100vh;
}
.pp-root a{color:var(--cyan);text-decoration:none}
.pp-root a:hover{color:var(--cyan-bright);text-decoration:underline}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}

/* HERO */
.pp-hero{max-width:900px;margin:0 auto;padding:60px 24px 32px;text-align:center;position:relative}

.pp-back{position:absolute;top:60px;left:24px;display:inline-flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.pp-back:hover{border-color:var(--cyan);color:var(--cyan)}
.pp-back svg{width:14px;height:14px}

.pp-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.pp-hero h1{font-size:clamp(32px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:12px}
.pp-updated{color:rgba(224,240,240,0.4);font-size:14px}

/* ARTICLE */
.pp-article{max-width:820px;margin:0 auto;padding:24px 24px 100px}

.pp-section{padding:28px 0;border-bottom:1px solid rgba(0,229,204,0.06)}
.pp-section:last-of-type{border-bottom:none}
.pp-section h2{font-size:22px;font-weight:700;color:#e0f0f0;margin-bottom:14px;letter-spacing:-.3px}
.pp-section p{font-size:15px;line-height:1.75;color:rgba(224,240,240,0.6);margin-bottom:12px}
.pp-section p:last-child{margin-bottom:0}

/* SUB-CARDS */
.pp-subcard{background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);border-radius:14px;padding:18px 22px;margin-top:12px}
.pp-subcard h3{font-size:15px;font-weight:700;color:var(--cyan);margin-bottom:6px}
.pp-subcard p{font-size:14px;line-height:1.65;color:rgba(224,240,240,0.55);margin:0}

/* CONTACT CARD */
.pp-contact-card{background:linear-gradient(135deg,rgba(0,229,204,0.06),rgba(34,217,127,0.03));border:1px solid rgba(0,229,204,0.15);border-radius:16px;padding:22px 26px;margin-top:16px;font-size:14px;line-height:1.9;color:rgba(224,240,240,0.65)}
.pp-contact-name{font-weight:700;color:#e0f0f0;font-size:15px;margin-bottom:4px}

/* RELATED */
.pp-related{margin-top:40px;padding-top:32px;border-top:1px solid rgba(0,229,204,0.06)}
.pp-related-link{display:inline-flex;align-items:center;gap:8px;padding:12px 24px;border-radius:10px;border:1px solid rgba(0,229,204,0.2);background:rgba(0,229,204,0.04);color:var(--cyan);font-weight:600;font-size:14px;transition:all .25s}
.pp-related-link:hover{background:rgba(0,229,204,0.1);border-color:var(--cyan);text-decoration:none;color:var(--cyan-bright)}
.pp-related-link svg{width:16px;height:16px;transition:transform .25s}
.pp-related-link:hover svg{transform:translateX(3px)}

@media(max-width:700px){
  .pp-back{position:static;margin-bottom:20px}
  .pp-hero{padding:40px 20px 24px}
}
`;
